import { useCallback, useEffect, useRef, useState } from 'react';

// Animované logo v úvodu landing page, převzaté z docs/graphic/trenigo-logo-animace.html (bez
// ovládacího panelu dema). Barvy loga žijí v LandingPage.css (světlý i tmavý motiv), tady jen třídy.

type Phase = 'pending' | 'playing' | 'done';

const EASE = 'cubic-bezier(.22,1,.36,1)';
const TOTAL_DURATION_MS = 4800;
const FALLBACK_ACCENT = 'rgb(5, 191, 134)';

// Nápis "Trenıgo" je SVG text bez webfontu - podle dostupného písma (Inter, Arial, Roboto…) se
// "ı" posouvá. Tečka se proto po načtení písem umístí nad skutečně vykreslené "ı".
const TITLE_FONT_SIZE = 250;
const TITLE_LETTER_SPACING = -10;
const DOTLESS_I_INDEX = 4;
// Posun tečky vpravo od středu "ı" kvůli kurzívě (vrchol dříku je vpravo), v násobcích velikosti písma.
const DOT_ITALIC_SHIFT_EM = 0.08;

// Částice kolem symbolu - deterministické pozice, při každém vykreslení stejné.
const PARTICLES = Array.from({ length: 42 }, (_, i) => ({
  x: (Math.sin(i * 137.5) + 1) / 2,
  y: (Math.cos(i * 76.3) + 1) / 2,
  r: 1 + (i % 3) * 0.65,
  p: i * 0.8,
}));

// Bez Web Animations API (jsdom, staré prohlížeče) nebo s omezeným pohybem se logo rovnou zobrazí
// v konečném stavu.
function canAnimate(): boolean {
  if (typeof Element === 'undefined' || typeof Element.prototype.animate !== 'function') return false;
  if (typeof window.matchMedia !== 'function') return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function alignDot(stage: HTMLElement) {
  const title = stage.querySelector<SVGTextElement>('.logo-title');
  const dot = stage.querySelector<SVGCircleElement>('.logo-dot');
  if (!title || !dot || typeof title.getStartPositionOfChar !== 'function') return;
  try {
    const start = title.getStartPositionOfChar(DOTLESS_I_INDEX).x;
    const next = title.getStartPositionOfChar(DOTLESS_I_INDEX + 1).x;
    const advance = next - start - TITLE_LETTER_SPACING;
    if (!Number.isFinite(advance) || advance <= 0) return;
    const cx = start + advance / 2 + DOT_ITALIC_SHIFT_EM * TITLE_FONT_SIZE;
    dot.setAttribute('cx', cx.toFixed(1));
  } catch {
    // Nevykreslený text (skrytá scéna) - tečka zůstane na výchozí pozici.
  }
}

export function LandingLogoHero() {
  // Předrenderované HTML i hydratace začínají ve stavu `pending` bez animace (FR01
  // 20261001_LandingPage/002_Marketing, FR-03): prostředí (Web Animations API,
  // prefers-reduced-motion) se zjistí až v efektu, jinak by se HTML ze serveru a první klientský
  // render lišily. S omezeným pohybem a bez JS ukáže hotové logo už `pending` stav přes CSS
  // (LandingPage.css, noscript styl v index.html), takže nic neproblikne.
  const [motion, setMotion] = useState(false);
  const [phase, setPhase] = useState<Phase>('pending');
  useEffect(() => {
    if (canAnimate()) {
      setMotion(true);
    } else {
      setPhase('done');
    }
  }, []);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationsRef = useRef<Animation[]>([]);
  const playingRef = useRef(false);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    let active = true;
    alignDot(stage);
    document.fonts?.ready
      .then(() => {
        if (active) alignDot(stage);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const play = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const pick = <T extends Element>(selector: string) => stage.querySelector<T>(selector);
    const arc = pick<SVGPathElement>('.logo-arc');
    const head = pick('.logo-head');
    const bodyParts = [...stage.querySelectorAll('.logo-body')];
    const title = pick('.logo-title');
    const dot = pick('.logo-dot');
    const subtitle = pick('.logo-subtitle');
    const caption = pick('.logo-caption');
    const progress = pick('.logo-progress span');
    if (!arc || !head || !title || !dot || !subtitle || !caption || !progress) return;

    animationsRef.current.forEach((animation) => animation.cancel());
    const animations: Animation[] = [];
    const run = (el: Element, frames: Keyframe[], delay: number, duration: number, easing = EASE) => {
      const animation = el.animate(frames, { duration, delay, fill: 'both', easing });
      animations.push(animation);
      return animation;
    };

    const length = arc.getTotalLength();
    run(
      arc,
      [
        { strokeDasharray: length, strokeDashoffset: length, opacity: 0 },
        { strokeDasharray: length, strokeDashoffset: 0, opacity: 1 },
      ],
      150,
      1350,
    );
    run(
      head,
      [
        { opacity: 0, transform: 'translateY(-70px) scale(.6)' },
        { opacity: 1, transform: 'translateY(0) scale(1)' },
      ],
      550,
      850,
    );
    bodyParts.forEach((part, i) =>
      run(
        part,
        [
          { opacity: 0, clipPath: 'inset(0 100% 0 0)', transform: 'translate(-45px,30px)' },
          { opacity: 1, clipPath: 'inset(0 0% 0 0)', transform: 'translate(0,0)' },
        ],
        700 + i * 250,
        1400,
      ),
    );
    run(
      title,
      [
        { opacity: 0, transform: 'translateX(100px)', filter: 'blur(12px)' },
        { opacity: 1, transform: 'translateX(0)', filter: 'blur(0px)' },
      ],
      1150,
      1450,
    );
    // Drobný doskok zelené tečky nad "i".
    run(
      dot,
      [
        { opacity: 0, transform: 'translateY(-100px)' },
        { opacity: 1, transform: 'translateY(10px)', offset: 0.65 },
        { opacity: 1, transform: 'translateY(-12px)', offset: 0.82 },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      1950,
      1000,
    );
    run(
      subtitle,
      [
        { opacity: 0, transform: 'translateY(25px)' },
        { opacity: 1, transform: 'translateY(0)' },
      ],
      2200,
      1000,
    );
    run(
      caption,
      [
        { opacity: 0, transform: 'translateY(10px)' },
        { opacity: 0.5, transform: 'translateY(0)' },
      ],
      2700,
      1000,
    );
    const clock = run(progress, [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], 0, TOTAL_DURATION_MS, 'linear');

    animationsRef.current = animations;
    playingRef.current = true;
    setPhase('playing');
    clock.finished
      .then(() => {
        playingRef.current = false;
        setPhase('done');
      })
      // Zrušení (opakované přehrání, odchod ze stránky) promise zamítne - není to chyba.
      .catch(() => {});
  }, []);

  // Animace se spustí při prvním zobrazení scény ve viewportu, jednou za načtení stránky.
  useEffect(() => {
    if (!motion) return;
    const stage = stageRef.current;
    if (!stage) return;
    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer?.disconnect();
            play();
          }
        },
        { threshold: 0.35 },
      );
      observer.observe(stage);
    } else {
      play();
    }
    return () => {
      observer?.disconnect();
      animationsRef.current.forEach((animation) => animation.cancel());
      animationsRef.current = [];
    };
  }, [motion, play]);

  // Částice a světelné stopy na pozadí; smyčka běží jen, když je scéna vidět.
  useEffect(() => {
    if (!motion) return;
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!stage || !canvas || !ctx) return;

    const accent = getComputedStyle(stage).getPropertyValue('--logo-accent').trim() || FALLBACK_ACCENT;
    let width = 0;
    let height = 0;
    let raf = 0;
    let last: number | null = null;
    let elapsed = 0;
    let visible = true;

    const resize = () => {
      const rect = stage.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * ratio;
      canvas.height = height * ratio;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    const frame = (now: number) => {
      if (last === null) last = now;
      elapsed += Math.min(now - last, 80);
      last = now;
      ctx.clearRect(0, 0, width, height);
      const t = elapsed / 1000;
      const energy = playingRef.current ? 1 : 0.35;
      ctx.fillStyle = accent;
      ctx.strokeStyle = accent;
      for (const p of PARTICLES) {
        ctx.globalAlpha = (0.12 + 0.12 * Math.sin(t * 0.6 + p.p)) * energy;
        ctx.beginPath();
        ctx.arc(p.x * width + Math.sin(t * 0.2 + p.p) * 12, p.y * height + Math.cos(t * 0.25 + p.p) * 10, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      for (let i = 0; i < 3; i++) {
        const rx = width * (0.19 + i * 0.024);
        const ry = height * (0.28 + i * 0.025);
        ctx.globalAlpha = 0.08 * energy;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(width * 0.25, height * 0.52, rx, ry, -0.5, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 0.35 * energy;
        const angle = t * 0.6 + i * 2.1;
        ctx.beginPath();
        ctx.arc(width * 0.25 + Math.cos(angle) * rx, height * 0.52 + Math.sin(angle) * ry, 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(frame);
    };

    resize();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    resizeObserver?.observe(stage);
    const visibilityObserver =
      'IntersectionObserver' in window
        ? new IntersectionObserver(([entry]) => {
            const next = entry?.isIntersecting ?? true;
            if (next && !visible) {
              last = null;
              raf = requestAnimationFrame(frame);
            } else if (!next && visible) {
              cancelAnimationFrame(raf);
            }
            visible = next;
          })
        : null;
    visibilityObserver?.observe(stage);
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      resizeObserver?.disconnect();
      visibilityObserver?.disconnect();
    };
  }, [motion]);

  return (
    <section className="logo-hero" id="top" aria-label="Trenigo SportAkademie">
      <div className="container">
        <div className="logo-stage" ref={stageRef} data-phase={phase}>
          <canvas className="logo-canvas" ref={canvasRef} aria-hidden="true" />
          <span className="logo-corner" aria-hidden="true">
            ENERGIE V POHYBU
          </span>
          <span className="logo-live" aria-hidden="true">
            TRENIGO
          </span>
          <div className="logo-wrap">
            <svg className="logo-svg" viewBox="0 0 1600 540" fill="none" role="img" aria-labelledby="landing-logo-title">
              <title id="landing-logo-title">Trenigo SportAkademie</title>
              <defs>
                <linearGradient id="landing-logo-green" x1="120" y1="390" x2="470" y2="85" gradientUnits="userSpaceOnUse">
                  <stop offset="0" className="logo-stop-green-start" />
                  <stop offset="1" className="logo-stop-green-end" />
                </linearGradient>
                <linearGradient id="landing-logo-mint" x1="115" y1="290" x2="345" y2="75" gradientUnits="userSpaceOnUse">
                  <stop offset="0" className="logo-stop-mint-start" />
                  <stop offset="1" className="logo-stop-mint-end" />
                </linearGradient>
              </defs>

              <g transform="translate(25 20)">
                <path
                  className="logo-arc logo-anim"
                  d="M105 285 A205 205 0 0 1 355 70"
                  stroke="url(#landing-logo-mint)"
                  strokeWidth={15}
                  strokeLinecap="round"
                />
                <circle className="logo-head logo-anim" cx="286" cy="145" r="31" fill="url(#landing-logo-green)" />
                <path
                  className="logo-body logo-anim"
                  fill="url(#landing-logo-green)"
                  d="M120 250 C176 219 232 207 301 211 C365 150 417 107 482 62 C424 144 375 224 323 295 C289 341 252 383 210 420 C174 451 137 470 96 482 C149 433 190 381 226 320 C255 271 268 241 249 223 C229 204 190 205 146 220 C135 224 126 235 120 250 Z"
                />
                <path
                  className="logo-body logo-anim"
                  fill="url(#landing-logo-green)"
                  d="M309 250 C365 255 412 285 430 338 C449 391 437 449 381 490 C408 438 405 387 383 345 C366 312 338 279 309 250 Z"
                />
              </g>

              <g transform="translate(525 0)">
                <text
                  className="logo-title logo-ink logo-anim"
                  x="0"
                  y="335"
                  fontFamily="Inter, Arial, Helvetica, sans-serif"
                  fontSize={TITLE_FONT_SIZE}
                  fontWeight="700"
                  fontStyle="italic"
                  letterSpacing={TITLE_LETTER_SPACING}
                >
                  Trenıgo
                </text>
                <circle className="logo-dot logo-accent logo-anim" cx="560" cy="156" r="24" />
                <text
                  className="logo-subtitle logo-anim"
                  x="445"
                  y="445"
                  textAnchor="middle"
                  fontFamily="Inter, Arial, Helvetica, sans-serif"
                  fontSize="82"
                  fontWeight="600"
                >
                  <tspan className="logo-ink">Sport</tspan>
                  <tspan className="logo-accent">Akademie</tspan>
                </text>
              </g>
            </svg>
          </div>
          <div className="logo-caption logo-anim" aria-hidden="true">
            Každý pohyb má svůj začátek.
          </div>
          <div className="logo-progress" aria-hidden="true">
            <span />
          </div>
          {motion && phase === 'done' && (
            <button type="button" className="logo-replay" onClick={play} aria-label="Přehrát animaci loga znovu">
              ↻ Přehrát znovu
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
